import { createAnthropic } from '@ai-sdk/anthropic'
import { createGoogleGenerativeAI } from '@ai-sdk/google'
import { createOpenAI } from '@ai-sdk/openai'
import { customProvider, type LanguageModel } from 'ai'
import { z } from 'zod/v4'

/** AI providers and their required env vars */

export const aiProviderSchema = z.enum(['openai', 'anthropic', 'gemini'])

export interface AIProviderSpec {
  baseURL: string

  authHeader: {
    name: string
    bearer: boolean
  }

  getTokensUsed: (response: unknown) => number

  // Path to the secret containing the API key in Parameter Store
  apiKeySecret: string
}

export type AIProvider = z.infer<typeof aiProviderSchema>

const providerSpecs = {
  openai: {
    baseURL: 'https://api.openai.com/v1',
    authHeader: {
      name: 'Authorization',
      bearer: true,
    },
    apiKeySecret: '/tunasong/openai/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          total_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.total_tokens
    },
  },
  anthropic: {
    baseURL: 'https://api.anthropic.com/v1',
    authHeader: {
      name: 'x-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/anthropic/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          input_tokens: z.number(),
          output_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.input_tokens + parsedResponse.usage.output_tokens
    },
  },
  gemini: {
    baseURL: 'https://generativelanguage.googleapis.com/v1beta',
    authHeader: {
      name: 'x-goog-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/google/gemini-api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usageMetadata: z.object({
          totalTokenCount: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usageMetadata.totalTokenCount
    },
  },
} satisfies Record<AIProvider, AIProviderSpec & Record<string, unknown>>

export const getProviderByModel = (model: string) => {
  for (const provider in supportedModelsByProvider) {
    if (supportedModelsByProvider[provider].includes(model)) {
      return provider as AIProvider
    }
  }
  return null
}

export const getMinuteCostByTranscriptionModel = (model: SupportedTranscribeModel) => transcribeModelCost[model]

export const getTokenCostByModel = (model: SupportedModel) => aiModelTokenCost[model]

export const getProviderSpec = (provider: AIProvider, type: 'client' | 'server' = 'server') => {
  const spec = providerSpecs[provider]
  if (type === 'client') {
    return {
      ...spec,
      baseURL: `https://tunasong.com/stream/proxy`,
    }
  }
  return spec
}

export const getProviderApiKeySecret = (provider: AIProvider) => providerSpecs[provider]?.apiKeySecret

const createProxyFetch = ({ model, authToken }: { model: string; authToken: string }) => {
  /** Proxy fetch to our API   */
  const proxyFetch: typeof fetch = async (input: RequestInfo | URL, options: RequestInit = {}) => {
    // Send the body, headers in the body. We do this to preserve the headers
    // and avoid CORS issues
    const url = typeof input === 'string' ? input : input.toString()

    const { headers, body } = input instanceof Request ? input : options

    // Remove the authorization header from the request

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        model,
        provider: getProviderByModel(model),
        payload: body,
        headers,
      }),
    })
  }
  return proxyFetch
}

/** Custom provider. authToken is JWT token in client mode, and API key in server mode */
const getAIModelInternal = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: string
}) => {
  const proxyFetch = createProxyFetch({ model, authToken })
  const baseURL = type === 'client' ? 'https://tunasong.com/stream/proxy' : undefined
  const apiKey = type === 'server' ? authToken : 'NOT_USED'

  /** Create custom providers with proxyFetch */
  const openai = createOpenAI({
    compatibility: 'strict',
    baseURL,
    apiKey,
    fetch: type === 'client' ? proxyFetch : fetch,
  })
  const anthropic = createAnthropic({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })
  const gemini = createGoogleGenerativeAI({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })

  const openAIOptions = { structuredOutputs: true }

  const transcribeModels = {
    'whisper-1': openai.transcription('whisper-1'),
    'gpt-4o-transcribe': openai.transcription('gpt-4o-transcribe'),
    'gpt-4o-mini-transcribe': openai.transcription('gpt-4o-mini-transcribe'),
  }

  const languageModels: Partial<Record<SupportedModel, LanguageModel>> = {
    // OpenAI models
    'gpt-4.1': openai('gpt-4.1', openAIOptions),
    'gpt-4': openai('gpt-4', openAIOptions),
    'gpt-4o': openai('gpt-4o', openAIOptions),
    'gpt-4o-mini': openai('gpt-4o-mini', openAIOptions),
    'gpt-o1': openai('gpt-o1', openAIOptions),
    'gpt-o1-mini': openai('gpt-o1-mini', openAIOptions),
    o1: openai('o1', openAIOptions),
    'o3-mini': openai('o3-mini', openAIOptions),
    'gpt-4-audio-preview': openai('gpt-4-audio-preview', openAIOptions),
    // Anthropic models
    'claude-sonnet-4-0': anthropic('claude-sonnet-4-0'),
    'claude-3-7-sonnet-latest': anthropic('claude-3-7-sonnet-latest'),
    'claude-3-5-sonnet-latest': anthropic('claude-3-5-sonnet-latest'),
    'claude-3-5-haiku-latest': anthropic('claude-3-5-haiku-latest'),
    'claude-3-opus-latest': anthropic('claude-3-opus-latest'),
    // Google Gemini models
    'gemini-2.0-flash': gemini('gemini-2.0-flash-exp'),
    'gemini-2.5-flash-preview-05-20': gemini('gemini-2.5-flash-preview-05-20'),
  }

  const provider = customProvider({
    languageModels,

    fallbackProvider: openai,
  })
  return { provider, openai, anthropic, gemini, languageModels, transcribeModels }
}

export const getAIModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  const { provider } = getAIModelInternal({ type, authToken, model })
  return provider.languageModel(model)
}

export const getTranscriptionModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedTranscribeModel
}) => {
  const { openai } = getAIModelInternal({ type, authToken, model })

  const transcriptionModel = openai.transcription(model)
  return transcriptionModel
}

// The cost is per minute of transcription
export const transcribeModelCost = {
  'whisper-1': 100,
  // @todo gpt-4o requires mp3 or wav
  // 'gpt-4o-transcribe': 100,
}

export type SupportedTranscribeModel = keyof typeof transcribeModelCost

/** This also defines the models  */
export const aiModelTokenCost = {
  'gpt-4.1': 10,
  'gpt-4.1-mini': 1,
  'gpt-4': 10,
  'gpt-4o': 2,
  'gpt-4o-mini': 0.5,
  'gpt-o1': 4,
  'gpt-4-audio-preview': 4,
  'gpt-o1-mini': 1,
  'whisper-1': 0.006,
  o1: 4,
  'o3-mini': 1,
  'o4-mini': 1,
  'claude-sonnet-4-0': 2,
  'claude-3-7-sonnet-latest': 2,
  'claude-3-5-sonnet-latest': 2,
  'claude-3-5-haiku-latest': 1,
  'claude-3-opus-latest': 1,
  'gemini-2.0-flash': 1,
  'gemini-2.5-flash-preview-05-20': 1,
}

export type SupportedModel = keyof typeof aiModelTokenCost

const supportedModelsByProvider: Record<string, [SupportedModel, ...SupportedModel[]]> = {
  openai: [
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-4',
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-o1',
    'gpt-o1-mini',
    'gpt-4-audio-preview',
    'o1',
    'o3-mini',
    'o4-mini',
    'whisper-1',
  ],
  anthropic: [
    'claude-sonnet-4-0',
    'claude-3-7-sonnet-latest',
    'claude-3-5-sonnet-latest',
    'claude-3-5-haiku-latest',
    'claude-3-opus-latest',
  ],
  gemini: ['gemini-2.0-flash', 'gemini-2.5-flash-preview-05-20'],
}

const aiModelNames: SupportedModel[] = Object.keys(aiModelTokenCost) as SupportedModel[]

export const aiModelsSchema = z.enum(aiModelNames as [SupportedModel, ...SupportedModel[]])
